import React, { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { clinicInfo } from '../mock';

const NAV_ITEMS = [
  { id: 'inicio', label: 'Inicio' },
  { id: 'servicios', label: 'Servicios' },
  { id: 'filosofia', label: 'Filosofía' },
  { id: 'equipo', label: 'Equipo' },
  { id: 'contacto', label: 'Contacto' }
];

const SCROLL_OFFSET = 130;

const scrollToSectionId = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    const top = element.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET;
    window.scrollTo({ top, behavior: 'smooth' });
  }
};

const desktopNavClass = (isActive) => {
  const base = 'relative text-xs font-medium uppercase tracking-[0.2em] transition-colors pb-1 after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:h-px after:bg-[#6E4F35] after:transition-all after:duration-300';
  const color = isActive ? 'text-[#6E4F35]' : 'text-gray-600 hover:text-[#6E4F35]';
  const underline = isActive ? 'after:w-full' : 'after:w-0 hover:after:w-full';
  return `${base} ${color} ${underline}`;
};

const MobileMenu = ({ activeSection, onNavigate }) => (
  <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
    <nav className="container mx-auto px-4 py-4 space-y-2">
      {NAV_ITEMS.map((item) => (
        <button
          key={item.id}
          data-testid={`mobile-nav-${item.id}`}
          onClick={() => onNavigate(item.id)}
          className={`block w-full text-left px-4 py-3 rounded-lg transition-colors ${
            activeSection === item.id
              ? 'bg-sage-50 text-sage-700 font-medium'
              : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          {item.label}
        </button>
      ))}
      <div className="pt-4 space-y-2">
        <a
          href={`tel:${clinicInfo.phone}`}
          className="flex items-center justify-center space-x-2 w-full px-4 py-3 text-sage-700 hover:bg-sage-50 rounded-lg transition-colors"
        >
          <Phone className="w-4 h-4" />
          <span className="font-medium">{clinicInfo.phone}</span>
        </a>
        <button
          onClick={() => onNavigate('contacto')}
          className="w-full px-4 py-3 text-sm font-medium uppercase tracking-[0.15em] text-[#6E4F35] border border-[#6E4F35]/40 rounded-full hover:bg-[#6E4F35] hover:text-cream-100 transition-all"
        >
          Reservar
        </button>
      </div>
    </nav>
  </div>
);

const Header = ({ activeSection, setActiveSection }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    scrollToSectionId(sectionId);
    setActiveSection(sectionId);
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile: logo centrado + hamburguesa */}
        <div className="md:hidden flex items-center justify-between h-20">
          <div className="w-10"></div>
          <button
            onClick={() => scrollToSection('inicio')}
            data-testid="header-logo-button"
            className="text-5xl font-serif font-bold text-gold-600 hover:text-gold-700 transition-colors"
            style={{ letterSpacing: '0.05em' }}
          >
            {clinicInfo.name}
          </button>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
            className="p-2 text-gray-700 hover:text-sage-600 transition-colors"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Desktop: logo centrado con navegación sutil debajo */}
        <div className={`hidden md:flex flex-col items-center transition-all duration-300 ${isScrolled ? 'py-3' : 'py-5'}`}>
          <button
            onClick={() => scrollToSection('inicio')}
            data-testid="header-logo-button-desktop"
            className={`font-serif font-bold text-gold-600 hover:text-gold-700 transition-all duration-300 ${
              isScrolled ? 'text-5xl' : 'text-6xl lg:text-7xl'
            }`}
            style={{ letterSpacing: '0.08em' }}
          >
            {clinicInfo.name}
          </button>

          <nav className={`flex items-center gap-10 transition-all duration-300 ${isScrolled ? 'mt-2' : 'mt-4'}`}>
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                data-testid={`nav-${item.id}`}
                className={desktopNavClass(activeSection === item.id)}
              >
                {item.label}
              </button>
            ))}
            <span className="h-4 w-px bg-gray-300"></span>
            <a
              href={`tel:${clinicInfo.phone}`}
              data-testid="header-phone-link"
              className="flex items-center gap-1.5 text-xs font-medium tracking-wider text-gray-600 hover:text-[#6E4F35] transition-colors"
            >
              <Phone className="w-3.5 h-3.5" />
              {clinicInfo.phone}
            </a>
            <button
              onClick={() => scrollToSection('contacto')}
              data-testid="header-reservar-button"
              className="text-xs font-medium uppercase tracking-[0.2em] text-[#6E4F35] border border-[#6E4F35]/40 hover:border-[#6E4F35] hover:bg-[#6E4F35] hover:text-cream-100 px-5 py-2 transition-colors duration-300"
            >
              Reservar
            </button>
          </nav>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && <MobileMenu activeSection={activeSection} onNavigate={scrollToSection} />}
    </header>
  );
};

export default Header;
